import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Card, Col, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import ServerError from '../../../../components/ServerError';
import { SkeletonTable } from '../../components/Skeleton/Table';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { useFlairBreadcrumbHook } from '../../../../hooks/useFlairBreadcrumbHook';
import { ApplyMeritControl } from './components/ApplyMeritControl';
import { BudgetConsumptionCard } from './components/BudgetConsumptionCard';
import { BudgetOverviewCard } from './components/BudgetOverviewCard';
import { CompensationReviewActions } from './components/CompensationReviewActions';
import { CompensationReviewInformation } from './components/CompensationReviewInformation';
import { CompensationReviewItemsTable } from './components/CompensationReviewItemsTable';
import { CompensationReviewResults } from './components/CompensationReviewResults';
import { CompensationReviewToolbar } from './components/CompensationReviewToolbar';
import {
  canEdit,
  isManagedInSalesforceOnly,
  recomputeLeafOverview,
} from './logic';
import { useCompensationReviewDetail } from './useCompensationReviews';
import './CompensationReviews.css';

type Props = {
  id: string;
  // URL of the list this detail belongs to (People subtab vs standalone).
  listUrl: string;
  // Builds a detail URL for a given review id, for drill-down links in the
  // budget consumption tree (People subtab vs standalone).
  buildDetailUrl: (reviewId: string) => string;
};

export const CompensationReviewDetail: React.FC<
  React.PropsWithChildren<Props>
> = ({ id, listUrl, buildDetailUrl }) => {
  const history = useHistory();
  const t = useNamespacedTranslation('compensationReviews');
  const { review, loading, error, actions } = useCompensationReviewDetail(id);
  const [filter, setFilter] = useState('');

  // The detail view is reused across :id changes (drill-down / reports-to
  // navigation) rather than remounted, so clear the item search when the
  // review changes — otherwise a previous search keeps hiding rows in the next
  // review's table.
  useEffect(() => {
    setFilter('');
  }, [id]);

  useFlairBreadcrumbHook([
    { label: t('title'), url: listUrl },
    { label: review?.name ?? '' },
  ]);

  const goToList = () => history.push(listUrl);

  const filteredItems = useMemo(() => {
    if (!review) {
      return [];
    }
    const search = filter.trim().toLowerCase();
    return search
      ? review.items.filter((item) =>
          item.employee.name.toLowerCase().includes(search),
        )
      : review.items;
  }, [review, filter]);

  if (error) {
    return <ServerError />;
  }
  if (loading && !review) {
    return (
      <>
        <PageHeader
          title={t('title')}
          goBackButtonAction={goToList}
          alignBackButton
        />
        <SkeletonTable columns={6} rows={5} />
      </>
    );
  }
  if (!review) {
    return (
      <>
        <PageHeader
          title={t('title')}
          goBackButtonAction={goToList}
          alignBackButton
        />
        <Alert variant="warning">{t('detail.notFound')}</Alert>
      </>
    );
  }

  const editable = canEdit(review);
  const showIncludeColumn = review.status === 'PREPARING';
  // Roll-up reviews aggregate their sub-tree and carry no items of their own;
  // leaves are where the line items (and submit) live.
  const isRollUp = !review.isLeaf;
  // On a leaf, recompute the overview from the reviewer's local edits so the
  // donut and the pre-submit caution reflect changes immediately; roll-ups keep
  // the server-computed overview.
  const overview =
    review.isLeaf && review.budgetOverview
      ? recomputeLeafOverview(review.budgetOverview, review.items)
      : review.budgetOverview;
  // A leaf reviewer over their own budget: submit is blocked until the total
  // is brought back within the available budget.
  const overBudget = editable && review.isLeaf && overview?.overBudget === true;
  // The sidebar holds the budget donut (any review with a budget). Without it
  // the main column is full-width.
  const hasSidebar = Boolean(review.hasBudget && overview);

  return (
    <>
      <PageHeader
        title={review.name}
        subtitle={t('title')}
        goBackButtonAction={goToList}
        alignBackButton
        actions={
          <CompensationReviewActions
            review={review}
            isProcessing={actions.isProcessing}
            submitDisabled={overBudget}
            submitDisabledTooltip={t('budget.overBudgetSubmitTooltip')}
            onSubmit={() => actions.submitForApproval()}
            onApprove={() => actions.approve()}
            onReject={() => actions.reject()}
          />
        }
      />

      {isManagedInSalesforceOnly(review) && (
        <Alert variant="info">{t('detail.salesforceOnlyNotice')}</Alert>
      )}

      <Row className="mb-4">
        <Col lg={hasSidebar ? 8 : 12} className="d-flex flex-column">
          <CompensationReviewInformation review={review} />
          {isRollUp && (
            <BudgetConsumptionCard
              nodes={review.budgetConsumption}
              buildDetailUrl={buildDetailUrl}
            />
          )}
          {/* On a leaf the results panel sits below the information card and
              grows to fill the column, balancing the taller budget donut. */}
          {review.isLeaf && (
            <CompensationReviewResults
              items={review.items}
              currency={review.currency}
            />
          )}
        </Col>
        {hasSidebar && overview && (
          <Col lg={4} className="d-flex flex-column">
            <BudgetOverviewCard
              overview={overview}
              assessingManagerName={review.assessingManager.name}
              buildDetailUrl={buildDetailUrl}
              fillHeight
            />
          </Col>
        )}
      </Row>

      {review.isLeaf && (
        <>
          {editable && (
            <ApplyMeritControl
              onApplyMeritToAll={(meritPercent) =>
                actions.applyMeritToAll(meritPercent)
              }
            />
          )}
          <Card>
            <Card.Body className="pb-0">
              <CompensationReviewToolbar
                filter={filter}
                onFilterChange={setFilter}
              />
            </Card.Body>
            <CompensationReviewItemsTable
              // Key by review id so the grid (and its local sort state) resets
              // to the default when navigating between reviews — the detail
              // component is reused across :id changes rather than remounted.
              key={review.id}
              items={filteredItems}
              currency={review.currency}
              editable={editable}
              showIncludeColumn={showIncludeColumn}
              onNewAmountChange={(itemId, newAmount) =>
                actions.setNewAmount(itemId, newAmount)
              }
              onIncludedChange={(itemId, included) =>
                actions.setIncluded(itemId, included)
              }
            />
          </Card>
        </>
      )}
    </>
  );
};
